/**
 * Eigen voorleesstemmen, in het tabblad LLM Config: een opname wordt een
 * stem, en elke stem kun je proefluisteren.
 *
 * De vaste stemmen van Mistral zijn Engels. Mistral volgt bij het voorlezen
 * de taal van de tekst, maar een stem klinkt het best in de taal waarin hij
 * is opgenomen. Een Nederlandse opname (een paar seconden is genoeg; 10 tot
 * 30 seconden rustig voorlezen is beter) geeft dus een stem die Nederlands
 * leest zonder Engels accent. Een nieuwe stem wordt meteen de voorleesstem.
 *
 * Een stem maken mag alleen met uitdrukkelijke toestemming van de spreker
 * (zo staat het in de regels van Mistral); daarom het vinkje.
 *
 * De taal van de opname: Nederlands, of een moedertaal waarin de vertaling
 * kan worden voorgelezen (stap 16). Een stem in een moedertaal wordt de stem
 * voor de vertaling in die taal, niet de voorleesstem; bijvoorbeeld van een
 * ouder of collega die de taal spreekt, met diens toestemming.
 */
import React, { useEffect, useState } from 'react';
import { Mic, Play, RefreshCw, Trash2 } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { VOORLEESSTEMMEN } from '../../../keuzelijsten';

export const PROEFZIN = 'Honingbijen leven samen in een volk van duizenden dieren. Elk volk heeft één koningin. '
  + 'De werksters vliegen van bloem naar bloem en halen nectar. Daar maken ze honing van.';

export function EigenStem({ config, onVeranderd }) {
  const stemmen = config.eigen_stemmen || [];
  const [naam, setNaam] = useState('');
  const [opname, setOpname] = useState(null);
  const [toestemming, setToestemming] = useState(false);
  const [taal, setTaal] = useState('Nederlands');
  const [bezig, setBezig] = useState(false);
  const [bericht, setBericht] = useState('');

  const [proefStem, setProefStem] = useState(config.voxtral_voice || VOORLEESSTEMMEN[0].value);
  const [proefTekst, setProefTekst] = useState(PROEFZIN);
  const [proefTempo, setProefTempo] = useState('gewoon');
  const [proefModel, setProefModel] = useState(config.voxtral_model || '');   // leeg: het gekozen model
  useEffect(() => { if (config.voxtral_model) setProefModel(config.voxtral_model); }, [config.voxtral_model]);
  const [luistert, setLuistert] = useState(false);
  const [geluid, setGeluid] = useState(null);       // de blob-URL van de laatste proef

  useEffect(() => { if (config.voxtral_voice) setProefStem(config.voxtral_voice); }, [config.voxtral_voice]);
  // Is de stem van de proef verwijderd, dan de stem die nu geldt.
  useEffect(() => {
    const bestaat = stemmen.some(s => s.id === proefStem) || VOORLEESSTEMMEN.some(s => s.value === proefStem);
    if (!bestaat) setProefStem(config.voxtral_voice || VOORLEESSTEMMEN[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen als de lijst verandert
  }, [stemmen.map(s => s.id).join(',')]);
  useEffect(() => () => { if (geluid) URL.revokeObjectURL(geluid); }, [geluid]);

  const maak = async (e) => {
    e.preventDefault();
    setBezig(true);
    setBericht('');
    try {
      const antwoord = await api.maakStem(naam.trim(), toestemming, opname, taal);
      const duur = antwoord.seconden ? ` (Mistral gebruikte ${Math.round(antwoord.seconden)} seconden van de opname)` : '';
      setBericht(`${antwoord.message}${duur}.`);
      setNaam('');
      setOpname(null);
      setToestemming(false);
      setTaal('Nederlands');
      e.target.reset();
      await onVeranderd();
    } catch (err) {
      setBericht('Fout: ' + err.message);
      // Duurde het te lang, dan kan de stem er toch zijn gekomen: de lijst opnieuw ophalen.
      onVeranderd().catch(() => {});
    } finally {
      setBezig(false);
    }
  };

  const verwijder = async (stem) => {
    if (!window.confirm(`De stem "${stem.naam}" verwijderen, ook bij Mistral?`)) return;
    setBericht('');
    try {
      await api.verwijderStem(stem.id);
      await onVeranderd();
    } catch (err) {
      setBericht('Fout: ' + err.message);
    }
  };

  const luister = async () => {
    setLuistert(true);
    setBericht('');
    try {
      const blob = await api.proefluister(proefStem, proefTekst, proefTempo, proefModel || undefined);
      setGeluid(URL.createObjectURL(blob));
    } catch (err) {
      setBericht('Fout: ' + err.message);
    } finally {
      setLuistert(false);
    }
  };

  const kanMaken = naam.trim() && opname && toestemming && !bezig;
  const moedertalen = config.voorlezen_moedertalen || [];
  /** Waarvoor een eigen stem nu gebruikt wordt: de voorleesstem, rustig, of de vertaling in een taal. */
  const gebruik = (stem) => [
    ...(config.voxtral_voice === stem.id ? ['voorlezen'] : []),
    ...(config.voxtral_voice_rustig === stem.id ? ['rustig'] : []),
    ...moedertalen.filter(m => m.stem === stem.id).map(m => m.taal),
  ];

  return (
    <div className="card mt-16" style={{ background: 'var(--vlak)' }}>
      <h3 className="mb-8"><Mic size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Eigen stem</h3>
      <p className="text-muted mb-16">
        De vaste stemmen zijn Engels. Mistral volgt de taal van de tekst, maar een stem klinkt het best in de taal
        waarin hij is opgenomen. Een Nederlandse opname geeft een stem die Nederlands leest zonder Engels accent.
      </p>

      {bericht && (
        <div className={`alert ${bericht.startsWith('Fout') ? 'alert-error' : 'alert-success'} mb-16`}>{bericht}</div>
      )}

      {stemmen.length > 0 && (
        <table className="mb-16">
          <tbody>
            {stemmen.map(stem => (
              <tr key={stem.id}>
                <td>{stem.naam}</td>
                <td className="text-muted">{stem.taal || 'Nederlands'}</td>
                <td>{gebruik(stem).length > 0 && <span className="badge badge-success">in gebruik: {gebruik(stem).join(', ')}</span>}</td>
                <td style={{ textAlign: 'right' }}>
                  <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px' }}
                    onClick={() => verwijder(stem)} title={`${stem.naam} verwijderen`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form onSubmit={maak} className="mb-16">
        <div className="grid-2">
          <div className="form-group">
            <label htmlFor="stem-naam">Naam van de stem</label>
            <input id="stem-naam" type="text" className="input" value={naam} onChange={e => setNaam(e.target.value)}
              placeholder="Marion" maxLength={100} />
          </div>
          <div className="form-group">
            <label htmlFor="stem-opname">Opname (wav, mp3, m4a, ...; hooguit 15 MB)</label>
            <input id="stem-opname" type="file" className="input"
              accept=".wav,.mp3,.m4a,.ogg,.flac,.webm,.aac,audio/*" onChange={e => setOpname(e.target.files[0] || null)} />
          </div>
          {moedertalen.length > 0 && (
            <div className="form-group">
              <label htmlFor="stem-taal">Taal van de opname</label>
              <select id="stem-taal" className="input" value={taal} onChange={e => setTaal(e.target.value)} style={{ maxWidth: 300 }}>
                <option value="Nederlands">Nederlands (wordt de voorleesstem)</option>
                {moedertalen.map(m => <option key={m.taal} value={m.taal}>{m.taal} (voor de vertaling in het {m.taal})</option>)}
              </select>
            </div>
          )}
        </div>
        <label className="mt-8" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={toestemming} onChange={e => setToestemming(e.target.checked)} />
          <span>De spreker heeft uitdrukkelijk toestemming gegeven om met deze opname een voorleesstem te maken.</span>
        </label>
        <button type="submit" className="btn btn-primary mt-8" disabled={!kanMaken}>
          {bezig ? <><RefreshCw size={16} className="spinning" /> Stem maken...</> : <><Mic size={16} /> Stem maken</>}
        </button>
      </form>

      <h4 className="mb-8">Proefluisteren</h4>
      <div className="form-group">
        <label htmlFor="proef-stem">Stem</label>
        <select id="proef-stem" className="input" value={proefStem} onChange={e => setProefStem(e.target.value)} style={{ maxWidth: 300 }}>
          {stemmen.length > 0 && (
            <optgroup label="Eigen stemmen">
              {stemmen.map(s => <option key={s.id} value={s.id}>{s.naam}{s.taal && s.taal !== 'Nederlands' ? ` (${s.taal})` : ''}</option>)}
            </optgroup>
          )}
          <optgroup label="Vaste stemmen (Engels)">
            {VOORLEESSTEMMEN.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </optgroup>
        </select>
      </div>
      {(config.voorleesmodellen || []).length > 1 && (
        <div className="form-group">
          <label htmlFor="proef-model">Model</label>
          <select id="proef-model" className="input" value={proefModel} onChange={e => setProefModel(e.target.value)} style={{ maxWidth: 300 }}>
            {config.voorleesmodellen.map(m => <option key={m.id} value={m.id}>{m.naam}</option>)}
          </select>
        </div>
      )}
      <div className="form-group">
        <label htmlFor="proef-tempo">Tempo</label>
        <select id="proef-tempo" className="input" value={proefTempo} onChange={e => setProefTempo(e.target.value)} style={{ maxWidth: 300 }}>
          <option value="gewoon">Gewoon</option>
          <option value="rustig">Rustig (pauze tussen de zinnen)</option>
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="proef-tekst">Tekst</label>
        <textarea id="proef-tekst" className="input" rows={3} value={proefTekst} onChange={e => setProefTekst(e.target.value)}
          style={{ fontFamily: 'inherit', resize: 'vertical' }} />
      </div>
      <button type="button" className="btn btn-secondary" onClick={luister} disabled={luistert || !proefTekst.trim()}>
        {luistert ? <><RefreshCw size={16} className="spinning" /> Even wachten...</> : <><Play size={16} /> Luister</>}
      </button>
      {geluid && <audio className="mt-8" src={geluid} controls autoPlay style={{ display: 'block', width: '100%' }} data-testid="proefgeluid" />}
    </div>
  );
}
